import type { Permission } from './permissions.js';

/**
 * Dokumentenzentrum (Quelle der Wahrheit für die Web-Seite): alle Druckdokumente und Berichte eines Basars bzw. einer
 * Organisation an einem Ort. Die Einträge verlinken nur auf vorhandene Seiten (Druckansichten, Berichtsseiten); das
 * Recht je Dokument ist das Recht der Quellroute. Der Server prüft ohnehin; die Liste zeigt nur, was die Person darf.
 */
export const DOCUMENT_LEVELS = ['event', 'org'] as const;
export type DocumentLevel = (typeof DOCUMENT_LEVELS)[number];

export const DOCUMENT_GROUPS = [
  'annahme',
  'verkauf',
  'abrechnung',
  'team',
  'werbung',
  'berichte',
  'organisation',
] as const;
export type DocumentGroup = (typeof DOCUMENT_GROUPS)[number];

export const DOCUMENT_GROUP_TITLES: Readonly<Record<DocumentGroup, string>> = {
  annahme: 'Annahme und Rückgabe',
  verkauf: 'Verkäufer',
  abrechnung: 'Abrechnung',
  team: 'Helfer und Schichten',
  werbung: 'Werbung',
  berichte: 'Berichte',
  organisation: 'Organisation',
};

export interface DocumentDefinition {
  readonly id: string;
  readonly level: DocumentLevel;
  readonly group: DocumentGroup;
  readonly title: string;
  readonly description: string;
  /** Recht der Quellroute (Organisation bzw. Basar aus dem Pfad). */
  readonly permission: Permission;
  /** Web-Pfad mit `:eventId` bzw. `:orgId`. */
  readonly path: string;
  /**
   * Ausgewiesene Formate: `print` Druckansicht („Als PDF speichern“), `xlsx` Excel-Datei (Download auf der Seite des Pfads),
   * `page` Seite mit Downloads. Ein Eintrag mit `print` und `xlsx` zeigt Druck- und Excel-Fassung derselben Auswertung.
   */
  readonly formats: readonly DocumentFormat[];
}

export const DOCUMENT_FORMATS = ['print', 'xlsx', 'page'] as const;
export type DocumentFormat = (typeof DOCUMENT_FORMATS)[number];

/** Zeigt an, ob ein Dokument eine Druckansicht hat. */
export function hasPrintFormat(document: DocumentDefinition): boolean {
  return document.formats.includes('print');
}

export const DOCUMENTS = [
  {
    id: 'return-list',
    level: 'event',
    group: 'annahme',
    title: 'Rückgabeliste',
    description: 'Die nicht verkauften Artikel je Verkäufer, zum Abhaken bei der Rückgabe.',
    permission: 'event.returns.sort',
    path: '/basare/:eventId/annahme/rueckgabeliste/druck',
    formats: ['print'],
  },
  {
    id: 'pickup-list',
    level: 'event',
    group: 'annahme',
    title: 'Abholliste („Noch nicht abgeholt“)',
    description: 'Verkäufer, die ihre Ware noch nicht abgeholt haben.',
    permission: 'event.items.receive',
    path: '/basare/:eventId/annahme/abholliste/druck',
    formats: ['print'],
  },
  {
    id: 'pickup-slip',
    level: 'event',
    group: 'annahme',
    title: 'Abholschein',
    description: 'Vier Scheine je Blatt mit großer Verkäufernummer, zum Ausschneiden.',
    permission: 'event.returns.sort',
    path: '/basare/:eventId/annahme/abholscheine/druck',
    formats: ['print'],
  },
  {
    id: 'defect-notice',
    level: 'event',
    group: 'annahme',
    title: 'Mängelzettel',
    description: 'Ankreuzpunkte für Mängel an abgegebener Ware (A5 quer).',
    permission: 'event.items.inspect',
    path: '/basare/:eventId/annahme/maengelzettel/druck',
    formats: ['print'],
  },
  {
    id: 'terms-sheet',
    level: 'event',
    group: 'annahme',
    title: 'Bedingungsblatt (je Verkäufer)',
    description: 'Zum Unterschreiben, zu finden in der Annahme bei jedem freigegebenen Verkäufer.',
    permission: 'event.items.receive',
    path: '/basare/:eventId/annahme',
    formats: ['page'],
  },
  {
    id: 'seller-list',
    level: 'event',
    group: 'verkauf',
    title: 'Verkäuferliste',
    description: 'Alle freigegebenen Verkäufer mit Nummer, Name und Artikelzahl.',
    permission: 'event.sellers.approve',
    path: '/basare/:eventId/verkaeufer/druck',
    formats: ['print'],
  },
  {
    id: 'settlement-receipts',
    level: 'event',
    group: 'abrechnung',
    title: 'Abrechnungen',
    description: 'Abrechnungsbelege je Verkäufer, nach dem Abrechnen. Keine steuerlichen Belege.',
    permission: 'event.settlement.run',
    path: '/basare/:eventId/abrechnung/druck',
    formats: ['print'],
  },
  {
    id: 'payout-list',
    level: 'event',
    group: 'abrechnung',
    title: 'Auszahlungsübersicht',
    description: 'Liste zum Abhaken bei der Barauszahlung.',
    permission: 'event.reports.export',
    path: '/basare/:eventId/abrechnung/auszahlung/druck',
    formats: ['print'],
  },
  {
    id: 'helper-list',
    level: 'event',
    group: 'team',
    title: 'Helferliste',
    description: 'Alle freigegebenen Helfer:innen.',
    permission: 'event.team.manage',
    path: '/basare/:eventId/helfer/liste/druck',
    formats: ['print'],
  },
  {
    id: 'shift-list',
    level: 'event',
    group: 'team',
    title: 'Schichtliste',
    description: 'Je Schicht eine Seite, mit Namen oder ohne.',
    permission: 'event.team.manage',
    path: '/basare/:eventId/helferschichten/liste/druck',
    formats: ['print'],
  },
  {
    id: 'poster',
    level: 'event',
    group: 'werbung',
    title: 'Plakat',
    description: 'Plakat des Basars zum Aushängen.',
    permission: 'event.manage',
    path: '/basare/:eventId/plakat',
    formats: ['page'],
  },
  {
    id: 'report-sales',
    level: 'event',
    group: 'berichte',
    title: 'Verkaufsjournal',
    description: 'Jeder einzelne Verkauf, chronologisch.',
    permission: 'event.reports.export',
    path: '/basare/:eventId/berichte/sales/druck',
    formats: ['print', 'xlsx'],
  },
  {
    id: 'report-items',
    level: 'event',
    group: 'berichte',
    title: 'Artikel',
    description: 'Alle Artikel mit Status, Verkaufszeit und Kasse.',
    permission: 'event.reports.export',
    path: '/basare/:eventId/berichte/items/druck',
    formats: ['print', 'xlsx'],
  },
  {
    id: 'report-cash',
    level: 'event',
    group: 'berichte',
    title: 'Kassen und Verkaufsübersicht',
    description:
      'Verkaufsübersicht je Kasse und Tag, Kassen-Abgleich und Bargeldabgleich. Ein Blatt je Kasse steht auf der Seite „Bargeld“. Keine Kassenprüfung im steuerlichen Sinn.',
    permission: 'event.reports.export',
    path: '/basare/:eventId/berichte/cash/druck',
    formats: ['print', 'xlsx'],
  },
  {
    id: 'report-conflicts',
    level: 'event',
    group: 'berichte',
    title: 'Konflikte',
    description: 'Doppelte Verkäufe und Preisabweichungen zur Klärung.',
    permission: 'event.reports.export',
    path: '/basare/:eventId/berichte/conflicts/druck',
    formats: ['print', 'xlsx'],
  },
  {
    id: 'report-sellers',
    level: 'event',
    group: 'berichte',
    title: 'Verkäufer-Auswertung',
    description: 'Nummer, Status, Artikel, Umsatz und Auszahlung je Verkäufer.',
    permission: 'event.reports.export',
    path: '/basare/:eventId/berichte/sellers/druck',
    formats: ['print', 'xlsx'],
  },
  {
    id: 'report-not-offered',
    level: 'event',
    group: 'verkauf',
    title: 'Lange nicht angeboten',
    description:
      'Frühere Verkäufer, die lange nicht mehr dabei waren und im Basar fehlen. Mit Namen und E-Mail (nur mit Recht für Kontaktdaten).',
    permission: 'event.contacts.view',
    path: '/basare/:eventId/berichte/not-offered/druck',
    formats: ['print', 'xlsx'],
  },
  {
    id: 'event-reports',
    level: 'event',
    group: 'berichte',
    title: 'Berichte und Auswertungen',
    description: 'Auswertungen mit Diagrammen und alle Berichte als Excel-Datei.',
    permission: 'event.reports.view',
    path: '/basare/:eventId/berichte',
    formats: ['page'],
  },
  {
    id: 'org-members',
    level: 'org',
    group: 'organisation',
    title: 'Mitglieder und Rollen',
    description: 'Name, E-Mail, Rolle und Mitglied seit (ohne Telefonnummern).',
    permission: 'org.members.manage',
    path: '/organisationen/:orgId/berichte/members/druck',
    formats: ['print', 'xlsx'],
  },
  {
    id: 'org-compare',
    level: 'org',
    group: 'berichte',
    title: 'Basar-Vergleich',
    description: 'Alle Basare der Organisation nebeneinander.',
    permission: 'org.reports.export',
    path: '/organisationen/:orgId/berichte/events/druck',
    formats: ['print', 'xlsx'],
  },
  {
    id: 'org-sellers-across',
    level: 'org',
    group: 'berichte',
    title: 'Verkäufer über mehrere Basare',
    description:
      'Teilnahmen und Umsatz je Basar, mit Namen und E-Mail (nur Inhaber und Verwalter).',
    permission: 'org.members.manage',
    path: '/organisationen/:orgId/berichte/sellers-across/druck',
    formats: ['print', 'xlsx'],
  },
  {
    id: 'org-fees',
    level: 'org',
    group: 'berichte',
    title: 'Gebührenübersicht je Basar',
    description: 'Verkaufssumme, Provision und Verkäufergebühr je Basar.',
    permission: 'org.reports.export',
    path: '/organisationen/:orgId/berichte/fees/druck',
    formats: ['print', 'xlsx'],
  },
  {
    id: 'org-returning',
    level: 'org',
    group: 'berichte',
    title: 'Beteiligung und Wiederkehrer',
    description: 'Freigegebene Verkäufer je Basar und wie viele davon schon früher dabei waren.',
    permission: 'org.reports.export',
    path: '/organisationen/:orgId/berichte/returning/druck',
    formats: ['print', 'xlsx'],
  },
  {
    id: 'org-letterhead',
    level: 'org',
    group: 'organisation',
    title: 'Briefkopf und Logo',
    description: 'Angaben und Logo, die auf allen Druckdokumenten der Organisation stehen.',
    permission: 'org.settings.edit',
    path: '/organisationen/:orgId/einstellungen',
    formats: ['page'],
  },
] as const satisfies readonly DocumentDefinition[];

export type DocumentId = (typeof DOCUMENTS)[number]['id'];

/** Dokumente einer Ebene, die die Person laut ihren Rechten sieht (leer = keine Kachel). */
export function documentsOf(
  level: DocumentLevel,
  permissions: readonly Permission[],
): readonly DocumentDefinition[] {
  return DOCUMENTS.filter(
    (document) => document.level === level && permissions.includes(document.permission),
  );
}

/** Setzt Basar bzw. Organisation in den Pfad ein (jede Kennung wird kodiert). */
export function documentPath(
  document: DocumentDefinition,
  ids: { eventId?: string; orgId?: string },
): string {
  return document.path.replace(/:(eventId|orgId)/g, (_match, name: string) => {
    const value = ids[name as 'eventId' | 'orgId'];
    if (value === undefined) throw new TypeError(`Kennung ${name} fehlt für ${document.id}.`);
    return encodeURIComponent(value);
  });
}
